import type { DateTimeValue } from '@elfsight/configurator-contracts';
import { setHours, setMinutes, setSeconds } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import type { ScheduleDefaultsType } from './schedule-defaults';
import { getDateFromControlValue } from './utils/get-date-obj';

export type RepeatType =
  | 'noRepeat'
  | 'daily'
  | 'weeklyOn'
  | 'weekday'
  | 'sameDayInMonth'
  | 'nthDayInMonth'
  | 'lastDayInMonth'
  | 'annuallyOn';

export type ScheduleSolversDisplayConfigType = {
  repeatType: RepeatType;
  daysOfWeek: number[];
  startDate: Date;
  endDate: Date;
  userDate: Date;
  freq: number | undefined;
  scheduleRepeatEnds: 'onDate' | 'afterOccurrences' | 'never';
  scheduleRepeatEndsDate: DateTimeValue<'datetime'> | undefined;
  scheduleRepeatEndsOccurrences: number | undefined;
  isCustom?: boolean;
};

export function getConfig(
  scheduleSettings: ScheduleDefaultsType,
  _debugUserDate?: Date
) {
  const {
    scheduleStart,
    scheduleEnd,
    scheduleTimeZone,
    scheduleRepeatPeriod,
    scheduleRepeatFrequency,
    scheduleRepeatInterval,
    scheduleRepeatMonthlyOnDay,
    scheduleRepeatWeeklyOnDays,
    scheduleIsAllDay,
    scheduleRepeatEnds,
    scheduleRepeatEndsOccurrences,
    scheduleRepeatEndsDate
  } = scheduleSettings;

  const userDate = scheduleTimeZone
    ? utcToZonedTime(_debugUserDate || new Date(), scheduleTimeZone)
    : new Date();

  let startDate = getDateFromControlValue(scheduleStart);
  let endDate = getDateFromControlValue(scheduleEnd);

  if (scheduleIsAllDay) {
    startDate = setMinutes(setHours(startDate, 0), 0);
    endDate = setSeconds(setMinutes(setHours(endDate, 23), 59), 59);
  }

  const customRepeatPeriod =
    scheduleRepeatFrequency === 'monthly'
      ? scheduleRepeatMonthlyOnDay
      : scheduleRepeatFrequency;

  const repeatType =
    scheduleRepeatPeriod === 'custom'
      ? customRepeatPeriod
      : scheduleRepeatPeriod;

  const resultFreq =
    scheduleRepeatPeriod === 'custom' ? scheduleRepeatInterval : 1;

  const daysOfWeekValue =
    scheduleRepeatPeriod === 'custom' ? scheduleRepeatWeeklyOnDays : [];

  const config: ScheduleSolversDisplayConfigType = {
    repeatType,
    startDate,
    endDate,
    userDate,
    daysOfWeek: daysOfWeekValue,
    freq: resultFreq,
    scheduleRepeatEnds,
    scheduleRepeatEndsOccurrences,
    scheduleRepeatEndsDate,
    isCustom: scheduleRepeatPeriod === 'custom'
  };

  return config;
}
